import {ApolloQueryResult, gql} from '@apollo/client';
import {intersect} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import AntibodySchemaConfiguration from 'antibodies/schema-configuration/AntibodySchemaConfiguration';
import {
  ANTIBODY_SCHEMA_INTERFACE_ID,
  DraftImplementedAntibodyInterface,
  isAntibodyImplementedInterface,
} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import {DomainGraphType} from 'app-core/framework/CoreObjectConstants';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  isRegistrySupportedType,
  isTagSchemaConstraint,
  isValidationRulesSupportedType,
  RegistryLabelingStrategy,
  MixtureSchemaConfig,
  SavingState,
  SequenceSchemaType,
  TagSchemaContainableType,
  TagSchemaSupportedFolderItemType,
  TagSchema,
  isEditableType,
} from 'constants/RegistryConstants';
import * as FileUtil from 'folder-items/FileUtil';
import {TagSchemaItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLArrayToREST, graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import TagSchemaDashboardLinkConfigTable from 'insights/embeddings/configuration/TagSchemaDashboardLinkConfigTable';
import {isRegistryEmbeddingEnabled} from 'insights/embeddings/IsEmbeddingEnabled';
import {EQUIPMENT_SCHEMA_INTERFACE_ID} from 'instruments/equipment/EquipmentConstants';
import navigateToURL from 'lib/navigateToURL';
import ComplexPolymerSchemaConfiguration from 'modbio/complex-polymers/ComplexPolymerSchemaConfiguration';
import {
  ComplexPolymerInterfaceApiId,
  DraftImplementedComplexPolymerInterface,
  isComplexPolymerImplementedInterface,
} from 'modbio/complex-polymers/ComplexPolymerSchemaInterfaceConstants';
import ContainerNameTemplateField from 'name-templates/ContainerNameTemplateField';
import EntityNameTemplateField from 'name-templates/EntityNameTemplateField';
import {NameTemplatePart} from 'name-templates/NameTemplateConstants';
import {graphQLNameTemplatePartsToProps} from 'name-templates/NameTemplateUtil';
import notifications from 'notifications';
import {ChildEntityAwareTagSchema, ChildEntityLinkConfig} from 'registry/child-entities/ChildEntityConstants';
import ChildEntitySchemaTable from 'registry/child-entities/ChildEntitySchemaTable';
import FieldTable, {ClientSchemaField} from 'registry/field/FieldTable';
import {CONFIRM_DELETE_ALL, CONFIRM_DELETE_FIELD} from 'registry/field/FieldTableConstants';
import FileFieldTableRowClass from 'registry/field/FileFieldTableRow';
import HoistedFieldTable from 'registry/field/HoistedFieldTable';
import {AllStorableSchemasQuery} from 'registry/graphql/__generated/AllStorableSchemasQuery';
import {
  RegistrySummaryQuery,
  RegistrySummaryQueryVariables,
} from 'registry/graphql/__generated/RegistrySummaryQuery';
import {ALL_STORABLE_SCHEMAS_QUERY} from 'registry/graphql/AllStorableSchemas';
import {REGISTRY_SUMMARY_QUERY} from 'registry/graphql/RegistrySummary';
import * as LabelingStrategiesUtil from 'registry/labeling-strategies/LabelingStrategiesUtil';
import LabelingStrategyConfiguration from 'registry/labeling-strategies/LabelingStrategyConfiguration';
import * as RegistryUtil from 'registry/RegistryUtil';
import {
  TRIAL_SAMPLE_SCHEMA_INTERFACE_ID,
  TRIAL_SAMPLE_SUPPORTED_TYPES,
  TRIAL_SUBJECT_SCHEMA_INTERFACE_ID,
} from 'registry/trial-interfaces/TrialInterfaceConstants';
import TrialSampleCheckbox from 'registry/trial-interfaces/TrialSampleCheckbox';
import TrialSubjectCheckbox from 'registry/trial-interfaces/TrialSubjectCheckbox';
import {StorableSchema} from 'samples/storables/StorableSchemaConstants';
import ExpirationCheckbox from 'samples/valid-contents/ExpirationCheckbox';
import FreezeThawCheckbox, {FreezeThawSettings} from 'samples/valid-contents/FreezeThawCheckbox';
import {
  EXPIRATION_TRACKING_ENTITY_SCHEMA_INTERFACE_ID,
  FREEZE_THAW_TRACKING_SCHEMA_INTERFACE_ID,
  FreezeThawStaticSchemaInterface,
  isFreezeThawInterfaceImplementation,
} from 'samples/valid-contents/ValidContentsConstants';
import {FIELD_TYPES, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {getNonArchivedOrComputedFields} from 'schemas/fields/SchemaFieldGetters';
import {AddSchemaInterfaceRequestSubmitParams} from 'schemas/schema-interface/AddSchemaInterfaceModal';
import InterfaceConstraintErrorsBannerStack from 'schemas/schema-interface/InterfaceConstraintErrorsBannerStack';
import {SchemaInterfaceSummary} from 'schemas/schema-interface/SchemaInterfaceConstants';
import {schemaInterfaceURL} from 'schemas/schema-interface/SchemaInterfaceDAO';
import {
  CreateSchemaInterfaceTable,
  EditSchemaInterfaceTable,
} from 'schemas/schema-interface/SchemaInterfaceTable';
import SetBySchemaInterfaceTooltip from 'schemas/schema-interface/SetBySchemaInterfaceTooltip';
import {schemaFieldsetName} from 'schemas/schema-interface/util/schemaFieldsetName';
import getSchemaInterfaceType from 'schemas/schema-interface/v2/getSchemaInterfaceType';
import {
  FIELD_LEVEL_ERRORS,
  getInterfaceConstraintErrorsForSchema,
  InterfaceConstraintErrorDisplayDataForSchema,
  SCHEMA_OR_INTERFACE_LEVEL_ERRORS,
} from 'schemas/schema-interface/validation/SchemaInterfaceValidationUtils';
import {ImplementedStaticSchemaInterface} from 'schemas/SchemaConstants';
import SchemaDescriptionInput from 'schemas/SchemaDescriptionInput';
import SchemaWarehouseNameField from 'schemas/SchemaWarehouseNameField';
import StaticInterfaceEntityFieldsBannerStack from 'schemas/static-schema-interface/StaticInterfaceEntityFieldsBannerStack';
import {
  itemTypeToFolderItemTypeAndSequenceType,
  getItemTypeDisplayName,
  getStaticSchemaInterfaceIdFromItemType,
  showStaticSchemaInterfaceAsType,
} from 'schemas/TagSchemaItemTypeUtil';
import useTagSchemaSummariesListingQuery from 'schemas/useTagSchemaSummariesListingQuery';
import ConstraintTable, {ConstraintTableType} from 'settings/registry/ConstraintTable';
import MixtureSchemaConfiguration from 'settings/registry/MixtureSchemaConfiguration';
import TagSchemaItemTypeSelector from 'settings/registry/TagSchemaItemTypeSelector';
import TagSchemaSampleTypeConfiguration from 'settings/registry/TagSchemaSampleTypeConfiguration';
import * as TagSchemaActions from 'tag-schema/TagSchemaActions';
import {useTagSchemaSavingState} from 'tag-schema/TagSchemaClientState';
import * as TagSchemaDAO from 'tag-schema/TagSchemaDAO';
import * as TagSchemaUtil from 'tag-schema/util/TagSchemaUtil';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {FOLDER_ITEM_ICONS_BY_ITEM_TYPE, FolderItemIcon} from 'ui-library/icons/IconConstants';
import IconPicker from 'ui-library/icons/IconPicker';
import SelectOptionModal from 'ui-library/modals/SelectOptionModal';
import runModal from 'ui-library/runModal';
import SettingsField, {SettingsLabel, SettingsPopover} from 'ui-library/SettingsField';
import * as modals from 'util/Modals';
import {capitalize, listToReadable} from 'util/NameUtil';
import {InputChangeEvent, Optional} from 'util/Types';
import {SimpleRule, SimpleRuleGraphQL} from 'validation_rules/components/simple_ui/types';
import {ValidationRule, AdvancedRuleGraphQL} from 'validation_rules/types';
import {isSimpleRulesEnabled} from 'validation_rules/utils';

import {RegistrySummaryGraphQL} from '../../../graphql-fragments/registry/__generated/RegistrySummaryGraphQL';
import {TagSchemaSummaryGraphQL} from '../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaSummaryGraphQL';
import {CurrentUserBenchlingAdminQuery} from './__generated/CurrentUserBenchlingAdminQuery';
import {
  TagSchemaFileSettingsQuery,
  TagSchemaFileSettingsQueryVariables,
} from './__generated/TagSchemaFileSettingsQuery';
import SetTagSchemaConstraintModal from './SetTagSchemaConstraintModal';

type ActiveMigration = ExtractByTypename<
  TagSchemaFileSettingsQuery['tagSchema'],
  'TagSchemaNode'
>['activeMigration'];

type QueryChildEntityAwareTagSchemaWithContainerNameTemplate = ExtractByTypename<
  TagSchemaFileSettingsQuery['tagSchema'],
  'TagSchemaNode'
>;

type QueryPrefixSource = NonNullable<QueryChildEntityAwareTagSchemaWithContainerNameTemplate['prefixSource']>;

type QueryChildEntityLinkConfig =
  QueryChildEntityAwareTagSchemaWithContainerNameTemplate['childEntityLinkConfigs'][number];

export type QueryImplementedAntibodySchemaInterface = NonNullable<
  TagSchemaFileSettingsQuery['implementedAntibodySchemaInterface']
>;

export type QueryComplexPolymerSchemaConfigurationData = NonNullable<
  TagSchemaFileSettingsQuery['complexPolymerSchemaConfiguration']
>;

export type QueryImplementedEquipmentSchemaInterface = NonNullable<
  TagSchemaFileSettingsQuery['implementedEquipmentSchemaInterface']
>;

export const TAG_SCHEMA_FILE_SETTINGS_QUERY = gql`
  # tagSchema.childEntityLinkConfigs does not query id
  # eslint-disable benchlint/id-field-required
  query TagSchemaFileSettingsQuery($tagSchemaId: StrictString!) @skipAudit @engComponent(name: REGISTRY) {
    tagSchema(id: $tagSchemaId) {
      ... on TagSchemaNode {
        schemaDescription
        containerNameTemplateParts {
          datetimeFormat
          fieldId
          text
          type
          displayName
          exampleValue
          typeSpecificConfiguration
        }
        ...TagSchemaGraphQL
        childEntityLinkConfigs {
          childEntitySchema {
            id
          }
          childSchemaLinkFieldJson
        }
        prefixSource {
          id
          name
        }
        labelingStrategiesOverrideSource {
          id
          name
        }
        nameTemplatesOverrideSource {
          id
          name
        }
        activeMigration {
          sourceField {
            id
            apiId
            name
          }
          destinationField {
            id
            apiId
            name
          }
          status
          numStartingFieldValues
          numMigratedFieldValues
          migrationType
        }
        numItems
        interfaceConfigs {
          interface {
            id
            ... on StaticSchemaInterfaceNode {
              id
            }
          }
        }
        itemType
        ...HasSimpleRulesGraphQL
        ...HasAdvancedRulesGraphQL
        constraint {
          typeSpecificConstraintConfig {
            constraintType
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
    # TODO (modbio): We should push these queries down to the modbio components that work with this data or use fragments.
    implementedAntibodySchemaInterface(schemaId: $tagSchemaId) {
      ...ImplementedAntibodySchemaInterfaceGraphQL
      ...InaccessibleInfoGraphQL
    }
    complexPolymerSchemaConfiguration(schemaId: $tagSchemaId) {
      ... on ComplexPolymerConfigInterface {
        id
        schemaInterface {
          id
        }
        ...OligoConjugateSchemaConfigurationGraphQL
        ...OligoDuplexSchemaConfigurationGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
    implementedEquipmentSchemaInterface(schemaId: $tagSchemaId) {
      ... on ImplementedEquipmentSchemaInterfaceNode {
        id
        schemaInterface {
          id
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ImplementedAntibodySchemaInterface').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('OligoConjugateSchemaConfiguration').fragment}
  ${getFragmentWrapper('OligoDuplexSchemaConfiguration').fragment}
  ${getFragmentWrapper('HasSimpleRules').fragment}
  ${getFragmentWrapper('HasAdvancedRules').fragment}
  ${getFragmentWrapper('TagSchema').fragment}
`;

export const CURRENT_USER_BENCHLING_ADMIN_QUERY = gql`
  query CurrentUserBenchlingAdminQuery @skipAudit @engComponent(name: REGISTRY) {
    currentUser {
      ... on UserNode {
        id
        isBenchlingAdmin
      }
    }
  }
`;

// The keys of create form state for information we want to display, but do not need to send on create
export const CREATE_FILE_SETTINGS_LOCAL_PARAM_KEYS: Array<keyof CreateFileSettingsLocalParams> = [
  'prefixSource',
  'labelingStrategiesOverrideSource',
  'nameTemplateParts',
  'nameTemplatesOverrideSource',
  'nameTemplatesOverrideFields',
];

export interface CreateFileSettingsLocalParams {
  prefixSource: SchemaInterfaceSummary | null;
  labelingStrategiesOverrideSource: SchemaInterfaceSummary | null;
  nameTemplateParts: Array<NameTemplatePart>;
  nameTemplatesOverrideSource: SchemaInterfaceSummary | null;
  nameTemplatesOverrideFields: Array<SchemaField | ClientSchemaField> | null;
}

export type CreateFileSettingParams = Pick<
  TagSchemaDAO.CreateTagSchemaParams,
  | 'constraint'
  | 'fields'
  | 'iconId'
  | 'isContainable'
  | 'mixtureSchemaConfig'
  | 'name'
  | 'prefix'
  | 'shouldCreateAsOligo'
  | 'showResidues'
  | 'useOrganizationCollectionAliasForDisplayLabel'
  | 'useRandomOrgAlias'
  | 'schemaInterfaceIds'
  | 'implementedStaticSchemaInterfaces'
  | 'schemaDescription'
> &
  Required<Pick<TagSchemaDAO.CreateTagSchemaParams, 'labelingStrategies' | 'includeRegistryIdInChips'>> & {
    itemType: TagSchemaItemType | null;
  } & CreateFileSettingsLocalParams;

export interface Props {
  ownerUrlId: string;
  registryId: string;
  tagSchemaId: string | null;
  interfaceConstraintErrors?: Array<InterfaceConstraintErrorDisplayDataForSchema>;
  createSchemaParams?: {fileSettingParams: CreateFileSettingParams};
  setCreateSchemaParams?: (params: CreateFileSettingParams) => void;
  // TODO (BNCH-66421): Remove once containerNameTemplateParts are included in the TagSchema fragment
  refetchTagSchemaFileSettings?: () => Promise<ApolloQueryResult<TagSchemaFileSettingsQuery>>;
}

type PrefixSource = Pick<QueryPrefixSource, 'id' | 'name'>;

interface TagSchemaWithAdditionalQueriedAttributes extends ChildEntityAwareTagSchema {
  containerNameTemplateParts: Array<NameTemplatePart>;
  prefixSource: PrefixSource | null;
  labelingStrategiesOverrideSource: SchemaInterfaceSummary | null;
  nameTemplatesOverrideSource: SchemaInterfaceSummary | null;
  implementedStaticSchemaInterfaces?: Array<ImplementedStaticSchemaInterface>;
  activeMigration: ActiveMigration | null;
  numItems: number;
  schemaDescription: string;
  itemType: TagSchemaItemType;
}

interface QueryingProps extends Props {
  tagSchema: TagSchemaWithAdditionalQueriedAttributes | null;
}

interface QueryingProps extends Props {
  saving: SavingState;
  tagSchema: TagSchemaWithAdditionalQueriedAttributes | null;
  isBenchlingAdmin: boolean;
  tagSchemas: Array<TagSchemaSummaryGraphQL>;
  storableSchemas: Array<StorableSchema>;
  registry: RegistrySummaryGraphQL;
}

interface State
  extends Omit<CreateFileSettingParams, 'registryId'>,
    Pick<
      ChildEntityAwareTagSchema,
      | 'archiveRecord'
      | 'containableType'
      | 'nameTemplateParts'
      | 'shouldOrderNamePartsBySequence'
      | 'sqlIdentifier'
    > {
  originalName?: string;
  containerNameTemplateParts: Array<NameTemplatePart>;
  labelingStrategiesOverrideSource: SchemaInterfaceSummary | null;
  nameTemplatesOverrideSource: SchemaInterfaceSummary | null;
  nameTemplatesOverrideFields: Array<SchemaField | ClientSchemaField> | null;
  tagSchemas: Array<TagSchemaSummaryGraphQL>;
  storableSchemas: Array<StorableSchema>;
  schemaInterfaceIds: Array<string>;
  implementedStaticSchemaInterfaces: Array<ImplementedStaticSchemaInterface>;
  interfaceConstraintErrors?: Array<InterfaceConstraintErrorDisplayDataForSchema>;
  activeMigration?: ActiveMigration;
  schemaDescription: string;
  prefixSource: SchemaInterfaceSummary | null;
}

export function getDefaultCreateFileSettingParams(
  initialFields?: Array<ClientSchemaField>
): CreateFileSettingParams {
  return {
    constraint: null,
    fields: initialFields ?? [],
    iconId: FolderItemIcon.BasicFolderItem,
    includeRegistryIdInChips: false,
    isContainable: false,
    labelingStrategies: LabelingStrategiesUtil.getInitialLabelingStrategies(),
    mixtureSchemaConfig: null,
    name: '',
    prefix: '',
    shouldCreateAsOligo: false,
    showResidues: false,
    useOrganizationCollectionAliasForDisplayLabel: false,
    useRandomOrgAlias: false,
    schemaInterfaceIds: [],
    implementedStaticSchemaInterfaces: [],
    schemaDescription: '',
    prefixSource: null,
    labelingStrategiesOverrideSource: null,
    nameTemplateParts: [],
    nameTemplatesOverrideSource: null,
    nameTemplatesOverrideFields: null,
    itemType: null,
  };
}

/**
 * React hook to track state of TagSchemaFileSettings when creating a new schema. This allows parent
 * components to restore the state of this component when being reconstructed.
 */
export function useCreateSchemaParams(
  initialFields?: Array<ClientSchemaField>
): [{fileSettingParams: CreateFileSettingParams}, (fileSettingParams: CreateFileSettingParams) => void] {
  const [fileSettingParams, setFileSettingParams] = React.useState<CreateFileSettingParams>(
    getDefaultCreateFileSettingParams(initialFields)
  );
  const setCreateSchemaParams = React.useCallback((params: CreateFileSettingParams) => {
    setFileSettingParams(params);
  }, []);

  return [{fileSettingParams}, setCreateSchemaParams];
}

export class TagSchemaFileSettings extends React.Component<QueryingProps, State> {
  constructor(props: QueryingProps) {
    super(props);
    this.state = this.refreshState();
  }

  refreshState(): State {
    return {
      ...this.defaultState(),
      ...this.getStateUpdateFromCreateParams(),
      ...(this.props.tagSchemaId == null
        ? {interfaceConstraintErrors: this.props.interfaceConstraintErrors}
        : {}),
      ...(this.props.tagSchemaId != null ? this.getStateUpdateFromTagSchema(this.props.tagSchema!) : {}),
    };
  }

  getStateUpdateFromCreateParams(): Partial<State> {
    if (!this.props.createSchemaParams) {
      return {};
    }
    return {
      ...this.props.createSchemaParams.fileSettingParams,
    };
  }

  defaultState(): State {
    return {
      ...getDefaultCreateFileSettingParams(),
      archiveRecord: null,
      constraint: null,
      containableType: TagSchemaContainableType.NONE,
      labelingStrategiesOverrideSource: null,
      nameTemplatesOverrideSource: null,
      nameTemplatesOverrideFields: null,
      nameTemplateParts: [],
      containerNameTemplateParts: [],
      originalName: 'New Schema',
      shouldOrderNamePartsBySequence: false,
      sqlIdentifier: '',
      tagSchemas: this.props.tagSchemas,
      storableSchemas: this.props.storableSchemas,
      prefixSource: this.props.tagSchema?.prefixSource ?? null,
    };
  }

  override componentDidUpdate(prevProps: QueryingProps, prevState: State): void {
    if (RegistryUtil.isSavingFinished(this.props.saving) && prevProps.saving !== this.props.saving) {
      _.defer(TagSchemaActions.resetSavingState);
      if (this.props.tagSchemaId) {
        this.setState(this.refreshState());
      }
    } else if (prevProps.tagSchemaId && prevProps.tagSchemaId !== this.props.tagSchemaId) {
      this.setState(this.refreshState());
    } else if (!isEqual(prevProps.createSchemaParams, this.props.createSchemaParams)) {
      this.setState(this.refreshState());
    } else if (prevProps.tagSchema !== this.props.tagSchema) {
      this.setState(this.refreshState());
    } else if (prevProps.interfaceConstraintErrors !== this.props.interfaceConstraintErrors) {
      this.setState(this.refreshState());
    }
  }

  private getStateUpdateFromTagSchema(tagSchema: TagSchemaWithAdditionalQueriedAttributes): Partial<State> {
    return {
      activeMigration: tagSchema.activeMigration,
      archiveRecord: tagSchema.archiveRecord,
      constraint: tagSchema.constraint,
      containableType: tagSchema.containableType,
      fields: tagSchema.allFields,
      iconId: tagSchema.iconId,
      includeRegistryIdInChips: tagSchema.includeRegistryIdInChips,
      isContainable: tagSchema.isContainable,
      labelingStrategies: tagSchema.labelingStrategies,
      labelingStrategiesOverrideSource: tagSchema.labelingStrategiesOverrideSource,
      nameTemplatesOverrideSource: tagSchema.nameTemplatesOverrideSource,
      mixtureSchemaConfig: tagSchema.mixtureSchemaConfig,
      name: tagSchema.name,
      nameTemplateParts: tagSchema.nameTemplateParts || [],
      containerNameTemplateParts: tagSchema.containerNameTemplateParts || [],
      originalName: tagSchema.name,
      prefix: tagSchema.prefix,
      schemaDescription: tagSchema.schemaDescription,
      shouldCreateAsOligo: tagSchema.shouldCreateAsOligo,
      shouldOrderNamePartsBySequence: tagSchema.shouldOrderNamePartsBySequence,
      showResidues: tagSchema.showResidues,
      implementedStaticSchemaInterfaces: tagSchema.implementedStaticSchemaInterfaces,
      sqlIdentifier: tagSchema.sqlIdentifier,
      useOrganizationCollectionAliasForDisplayLabel: tagSchema.useOrganizationCollectionAliasForDisplayLabel,
      useRandomOrgAlias: tagSchema.useRandomOrgAlias,
      itemType: tagSchema.itemType,
    };
  }

  private setInterfaceConstraintErrors = (
    interfaceConstraintErrors: Array<InterfaceConstraintErrorDisplayDataForSchema>
  ): void => {
    this.setState({interfaceConstraintErrors});
  };

  isWritable(): boolean {
    return (
      !this.state.archiveRecord &&
      (this.props.tagSchema == null || this.props.tagSchema.permissions.definitionEditable === true)
    );
  }

  isConstraintWritable(): boolean {
    if (!this.isWritable()) {
      return false;
    }
    // A ClientTagSchemaConstraint is writable. A (persisted) TagSchemaConstraint is writable if it is not
    // inherited from an interface, aka its source is null.
    if (this.state.constraint && isTagSchemaConstraint(this.state.constraint)) {
      return this.state.constraint.source == null;
    }
    return true;
  }

  private getFolderItemType(): TagSchemaSupportedFolderItemType | undefined {
    if (!this.state.itemType) {
      return undefined;
    }

    return itemTypeToFolderItemTypeAndSequenceType(this.state.itemType).folderItemType;
  }

  updateStateAndSchema<K extends keyof State>(
    newState: Pick<State, K>,
    {deleteOldSchemaTags}: {deleteOldSchemaTags?: boolean} = {}
  ): void {
    // eslint-disable-next-line no-restricted-syntax
    const mergedNewState = _.extend({}, this.state, newState);
    const bioentityLinkFieldPresent = (bioentityFieldType: string): boolean =>
      mergedNewState.fields.filter((field: SchemaField) => field.fieldType === bioentityFieldType).length > 0;
    const invalidLinks = [];
    // Transcription links are invalid when the entity is not a DNA Sequence
    if (
      bioentityLinkFieldPresent(FIELD_TYPES.TRANSCRIPTION_LINK.type) &&
      mergedNewState.itemType !== TagSchemaItemType.DNA_SEQUENCE
    ) {
      invalidLinks.push('transcription');
    }
    // Part and Translation links are invalid when the entity is not a long sequence
    if (
      mergedNewState.itemType !== TagSchemaItemType.DNA_SEQUENCE &&
      mergedNewState.itemType !== TagSchemaItemType.RNA_SEQUENCE
    ) {
      if (bioentityLinkFieldPresent(FIELD_TYPES.PART_LINK.type)) {
        invalidLinks.push('part');
      }
      if (bioentityLinkFieldPresent(FIELD_TYPES.TRANSLATION_LINK.type)) {
        invalidLinks.push('translation');
      }
    }
    // AA Part links are invalid when the entity is not a Protein
    if (
      bioentityLinkFieldPresent(FIELD_TYPES.AA_PART_LINK.type) &&
      mergedNewState.itemType !== TagSchemaItemType.AA_SEQUENCE
    ) {
      invalidLinks.push('AA part');
    }
    if (invalidLinks.length > 0) {
      // eslint-disable-next-line no-restricted-syntax
      invalidLinks.sort(); // Ignoring the suggested functions, which are for key value arrays
      const invalidLinksCapitalized = [...invalidLinks];
      invalidLinksCapitalized[0] = capitalize(invalidLinksCapitalized[0]);
      notifications.error(
        `${listToReadable(invalidLinksCapitalized)} links are not supported for this entity type. \
        All ${listToReadable(
          invalidLinks
        )} link fields must be changed or deleted before changing the schema to this type.`
      );
      return;
    }
    if (this.props.tagSchemaId != null) {
      this.handleUpdateSchema(newState, deleteOldSchemaTags);
    } else {
      this.setState(newState, () => {
        this.updatePropertiesForCreate();
      });
    }
  }

  private handleUpdateSchema(newState: Partial<State>, deleteOldSchemaTags?: boolean): void {
    // Remove undefined values b/c update endpoint expects params present only when being modified
    // eslint-disable-next-line no-restricted-syntax
    const updateParams = _.omit(
      {
        ...newState,
        deleteOldSchemaTags,
        fields: newState.fields ? RegistryUtil.sanitizeFields(newState.fields) : undefined,
      },
      (param: unknown) => param === undefined
    );
    assert(this.props.tagSchemaId != null, 'Expected tagSchemaId to be non-null during update');

    TagSchemaActions.update(this.props.tagSchemaId, updateParams)
      .done(() => {
        this.setState({interfaceConstraintErrors: []});
        // Some attributes for props.tagSchema are hydrated via GraphQL (see QueryingTagSchemaFileSettings)
        // rather than from the REST tagSchema response, so we proactively refetch on all updates
        this.props.refetchTagSchemaFileSettings!();
      })
      .fail((response) => {
        const interfaceConstraintErrors = getInterfaceConstraintErrorsForSchema(response);
        if (interfaceConstraintErrors != null) {
          this.setInterfaceConstraintErrors(interfaceConstraintErrors);
        }
      });
  }

  private updatePropertiesForCreate(): void {
    assert(
      this.props.setCreateSchemaParams != null,
      'setCreateSchemaParams prop should be set when creating a schema'
    );

    const properties = {
      constraint: this.state.constraint,
      fields: this.state.fields,
      iconId: this.state.iconId,
      implementedStaticSchemaInterfaces: this.state.implementedStaticSchemaInterfaces,
      includeRegistryIdInChips: this.state.includeRegistryIdInChips,
      isContainable: this.state.isContainable,
      labelingStrategies: this.state.labelingStrategies,
      mixtureSchemaConfig: this.state.mixtureSchemaConfig,
      name: this.state.name,
      prefix: this.state.prefix,
      shouldCreateAsOligo: this.state.shouldCreateAsOligo,
      showResidues: this.state.showResidues,
      useOrganizationCollectionAliasForDisplayLabel: this.state.useOrganizationCollectionAliasForDisplayLabel,
      useRandomOrgAlias: this.state.useRandomOrgAlias,
      schemaInterfaceIds: this.state.schemaInterfaceIds,
      schemaDescription: this.state.schemaDescription,
      prefixSource: this.state.prefixSource,
      labelingStrategiesOverrideSource: this.state.labelingStrategiesOverrideSource,
      nameTemplateParts: this.state.nameTemplateParts,
      nameTemplatesOverrideSource: this.state.nameTemplatesOverrideSource,
      nameTemplatesOverrideFields: this.state.nameTemplatesOverrideFields,
      itemType: this.state.itemType,
    };
    this.props.setCreateSchemaParams(properties);
  }

  private handleChangePrefix = (prefix: string): void => {
    this.updateStateAndSchema({prefix});
  };

  private handleChangeName = (name: string): void => {
    this.updateStateAndSchema({name});
  };

  private handleChangeSqlIdentifier = (sqlIdentifier: string): void => {
    this.updateStateAndSchema({sqlIdentifier});
  };

  private handleChangeIcon = (iconId: BenchlingIconName): void => {
    this.updateStateAndSchema({iconId});
  };

  private handleChangeShowResidues = (e: InputChangeEvent): void => {
    const newState = {showResidues: e.target.checked};
    this.updateStateAndSchema(newState);
  };

  private handleChangeShouldCreateAsOligo = (e: InputChangeEvent): void => {
    const newState = {shouldCreateAsOligo: e.target.checked};
    this.updateStateAndSchema(newState);
  };

  private handleChangeUseRandomOrgAlias = (e: InputChangeEvent): void => {
    const newState = {useRandomOrgAlias: e.target.checked};
    this.updateStateAndSchema(newState);
  };

  private handleChangeMixtureSchemaConfig = (newConfig: MixtureSchemaConfig): void => {
    this.updateStateAndSchema({mixtureSchemaConfig: newConfig});
  };

  private handleChangeUseOrganizationRegistryIdentifierForDisplayLabel = (e: InputChangeEvent): void => {
    const newState = {useOrganizationCollectionAliasForDisplayLabel: e.target.checked};
    this.updateStateAndSchema(newState);
  };

  private handleChangeIncludeRegistryIdInChips = (e: InputChangeEvent): void => {
    const newState = {includeRegistryIdInChips: e.target.checked};
    this.updateStateAndSchema(newState);
  };

  private handleChangeLabelingStrategies = (labelingStrategies: Array<RegistryLabelingStrategy>): void => {
    this.updateStateAndSchema({labelingStrategies});
  };

  handleChangeFileType = (selectedItemType: TagSchemaItemType | null): void => {
    if (selectedItemType == null) {
      return;
    }

    const {folderItemType} = itemTypeToFolderItemTypeAndSequenceType(selectedItemType);
    type FileTypeSpec = Pick<State, 'itemType'>;
    type StateSpec =
      | FileTypeSpec
      | (FileTypeSpec &
          Pick<
            State,
            | 'iconId'
            | 'showResidues'
            | 'shouldCreateAsOligo'
            | 'mixtureSchemaConfig'
            | 'implementedStaticSchemaInterfaces'
            | 'fields'
          >);
    let newState: StateSpec = {itemType: selectedItemType};

    // If creating a schema, always reset icon to the default for that file type
    // If editing, only reset icon if the current icon is not supported for the selected item type
    if (
      this.props.tagSchemaId == null ||
      !(FOLDER_ITEM_ICONS_BY_ITEM_TYPE[selectedItemType] as Array<string>).includes(this.state.iconId)
    ) {
      newState = {
        ...newState,
        iconId: FOLDER_ITEM_ICONS_BY_ITEM_TYPE[selectedItemType][0],
      };
    }

    if (selectedItemType === TagSchemaItemType.MIXTURE && this.state.mixtureSchemaConfig == null) {
      // If creating a mixture schema, set a default value for mixture schema config values
      newState = {
        ...newState,
        mixtureSchemaConfig: {
          allowMeasuredIngredients: false,
          componentLotStorageEnabled: null,
          componentLotTextEnabled: null,
        },
      };
    } else if (selectedItemType !== TagSchemaItemType.MIXTURE) {
      // Ensure that mixture-schema-specific attributes get cleared out for a non-mixture schema
      newState = {...newState, mixtureSchemaConfig: null};
    }

    // Reset showResidues state if selected item type does not support residues
    if (folderItemType && !TagSchemaUtil.entityTypeSupportsResidues(folderItemType)) {
      newState = {...newState, showResidues: false};
    }

    if (folderItemType !== ModelTypeConstants.SEQUENCE) {
      newState = {...newState, shouldCreateAsOligo: false};
    }

    const updatedImplementedStaticSchemaInterfaces = this.state.implementedStaticSchemaInterfaces.filter(
      (interfaceData) => !showStaticSchemaInterfaceAsType(interfaceData.interfaceId)
    );
    const staticSchemaInterfaceId = getStaticSchemaInterfaceIdFromItemType(selectedItemType);
    if (staticSchemaInterfaceId) {
      updatedImplementedStaticSchemaInterfaces.push({interfaceId: staticSchemaInterfaceId});
    }

    newState = {...newState, implementedStaticSchemaInterfaces: updatedImplementedStaticSchemaInterfaces};

    if (!isValidationRulesSupportedType(selectedItemType)) {
      const fieldsWithoutRules = this.state.fields.map((field) => ({
        ...field,
        simpleRules: [],
      }));
      newState = {...newState, fields: fieldsWithoutRules};
    }

    this.updateStateAndSchema(newState);
  };

  private handleRemoveConstraint = (): void => {
    modals
      .confirm('Remove constraint', 'Are you sure you want to remove this constraint?', 'danger')
      .done(() => {
        this.updateStateAndSchema({constraint: null});
      });
  };

  private handleOpenConstraintModal = async (): Promise<void> => {
    const folderItemType = this.getFolderItemType();
    if (!folderItemType) {
      notifications.error('Please select an entity type before adding a constraint.');
      return;
    }
    const nonComputedOrArchivedFields = getNonArchivedOrComputedFields(this.state.fields);
    const constraint = await runModal(SetTagSchemaConstraintModal, {
      bioentityType: folderItemType,
      itemType: this.state.itemType,
      schemaFields: nonComputedOrArchivedFields,
      tableType: ConstraintTableType.SCHEMA,
    });
    if (constraint == null) {
      return;
    }
    if (this.state.constraint != null) {
      notifications.error('You may not add more than one constraint.');
    } else {
      const newState = {constraint};
      this.updateStateAndSchema(newState);
    }
  };

  private setNameTemplateParts = async (
    nameTemplateParts: Array<NameTemplatePart>,
    shouldOrderNamePartsBySequence: boolean
  ): Promise<void> => {
    assert(
      this.props.tagSchemaId != null,
      'Expected tagSchemaId to be non-null when setting name template parts'
    );
    await TagSchemaActions.setNameTemplateParts(
      this.props.tagSchemaId,
      nameTemplateParts,
      shouldOrderNamePartsBySequence
    );
    this.props.refetchTagSchemaFileSettings!();
  };

  private setContainerNameTemplateParts = async (
    nameTemplateParts: Array<NameTemplatePart>
  ): Promise<boolean> => {
    // Returns true if the name template modal should be closed, false otherwise.
    assert(
      this.props.tagSchemaId != null,
      'Expected tagSchemaId to be non-null when setting container name template parts'
    );
    return await TagSchemaActions.setContainerNameTemplatePartsWithDataMods(
      this.props.tagSchemaId,
      nameTemplateParts,
      async () => {
        await this.props.refetchTagSchemaFileSettings!();
      }
    );
  };

  private handleFieldsChange = (
    newFields: Array<ClientSchemaField>,
    param: {deleteOldSchemaTags?: boolean; renameOldSchemaTags?: boolean}
  ): void => {
    if (param == null) {
      param = {};
    }
    const {deleteOldSchemaTags} = param;
    const newState = {fields: newFields};
    this.updateStateAndSchema(newState, {deleteOldSchemaTags});
  };

  private isBioentitySchema(): boolean {
    const folderItemType = this.getFolderItemType();
    return folderItemType != null && isRegistrySupportedType(folderItemType);
  }

  private shouldShowIsRequired(): boolean {
    return BenchlingConfig.VALIDATION_RULES_ENGINE_ENABLE_M3
      ? false
      : this.props.registry.useStrictValidation;
  }

  private shouldShowMultiSelect(): boolean {
    return !BenchlingConfig.VALIDATION_RULES_ENGINE_ENABLE_M3;
  }

  private shouldShowValidations(): boolean {
    return BenchlingConfig.VALIDATION_RULES_ENGINE_ENABLE_M3;
  }

  private renderFileFields(
    folderItemType: TagSchemaSupportedFolderItemType | undefined,
    sequenceType: SequenceSchemaType | null
  ): JSX.Element {
    return (
      <div className="u-large-spacing-bottom">
        <FieldTable // TODO (BNCH-53233): Render FieldTableLoading while fetching data
          isBeingCreated={this.props.tagSchemaId == null}
          isDisabled={!this.isWritable()}
          schemaId={this.props.tagSchemaId}
          schema={this.props.tagSchema}
          fields={this.state.fields}
          itemType={this.state.itemType}
          fileType={folderItemType}
          sequenceType={sequenceType}
          itemClass={FileFieldTableRowClass}
          schemaNumItems={this.props.tagSchema?.numItems}
          shouldShowIsParentLink={this.isBioentitySchema() && BenchlingConfig.REGISTRY_ENABLE_CHILD_ENTITIES}
          shouldShowIsRequired={this.shouldShowIsRequired()}
          shouldShowMultiSelect={this.shouldShowMultiSelect()}
          shouldShowValidations={this.shouldShowValidations()}
          shouldShowIsIntegration={
            BenchlingConfig.SCHEMAS_ENABLE_INTEGRATION_FIELDS && this.props.isBenchlingAdmin
          }
          onFieldsChange={this.handleFieldsChange}
          batchLinkProps={{entityToBatchLink: true}} // Used for rendering the correct batch link label
          storableSchemas={this.state.storableSchemas}
          registryId={this.props.registryId}
          ownerUrlId={this.props.ownerUrlId}
          ownerId={this.props.registry.ownerJson.id}
          savingState={this.props.saving}
          schemaType="entity"
          handleDeleteConfirmation={(onSubmit: () => void) => {
            SelectOptionModal.open({
              options: [
                {text: 'Preserve as custom fields.', value: CONFIRM_DELETE_FIELD},
                {
                  text: 'Delete across all entities with this field. This action cannot be undone.',
                  value: CONFIRM_DELETE_ALL,
                },
              ],
              preamble: `Would you like to delete this schema field across all entities with this \
    field, or preserve the field values as custom fields? Deleting across all entities cannot be undone.`,
              title: 'Delete Field',
              onSubmit,
            });
          }}
          activeMigration={this.props.tagSchema?.activeMigration}
          refetchSchemaAfterUpdate={async () => {
            await this.props.refetchTagSchemaFileSettings!();
          }}
          isFieldMigrationEnabled={
            BenchlingConfig.SCHEMAS_ENABLE_SCHEMA_FIELD_MIGRATION ||
            BenchlingConfig.SCHEMAS_ENABLE_MANUAL_SCHEMA_FIELD_MIGRATION
          }
          shouldReplaceFieldsFromProps={true}
        />
      </div>
    );
  }

  private renderHoistedFields(): JSX.Element | null {
    if (!this.props.tagSchemaId) {
      return null;
    }
    const alreadyHasHoistedFields =
      this.props.tagSchema &&
      this.props.tagSchema.hoistedTagSchemaGraphs &&
      this.props.tagSchema.hoistedTagSchemaGraphs.length > 0;
    if (!this.props.registry.useStrictValidation || !alreadyHasHoistedFields) {
      return null;
    }
    return (
      <div className="u-large-spacing-bottom">
        <HoistedFieldTable tagSchemaId={this.props.tagSchemaId} isDisabled={!this.isWritable()} />
      </div>
    );
  }

  private handleUpdateStaticSchemaInterfaceData = (
    updatedInterfaceData: ImplementedStaticSchemaInterface
  ): void => {
    const otherExistingInterfaces = this.state.implementedStaticSchemaInterfaces.filter(
      (ssi) => ssi.interfaceId !== updatedInterfaceData.interfaceId
    );

    const newInterfaces = [...otherExistingInterfaces, updatedInterfaceData];
    this.updateStateAndSchema({
      implementedStaticSchemaInterfaces: newInterfaces,
    });
  };

  private getAntibodyInterfaceImplementation = (): DraftImplementedAntibodyInterface | null => {
    const antibodyConfigs = (this.state.implementedStaticSchemaInterfaces ?? []).flatMap((implementation) =>
      isAntibodyImplementedInterface(implementation) ? [implementation] : []
    );
    assert(antibodyConfigs.length <= 1, 'Expected only one antibody interface implementation per schema');
    return antibodyConfigs[0] ?? null;
  };

  private getComplexPolymerInterfaceImplementation = (): DraftImplementedComplexPolymerInterface | null => {
    const complexPolymerConfigs = (this.state.implementedStaticSchemaInterfaces ?? []).flatMap(
      (implementation) => (isComplexPolymerImplementedInterface(implementation) ? [implementation] : [])
    );
    assert(
      complexPolymerConfigs.length <= 1,
      'Expected only one complex polymer interface implementation per schema'
    );
    return complexPolymerConfigs[0] ?? null;
  };

  private handleFreezeThawInterfaceImplementation = (settings: FreezeThawSettings): void => {
    const interfacesWithoutFreezeThaw = (this.state.implementedStaticSchemaInterfaces ?? []).filter(
      (implementation) => !isFreezeThawInterfaceImplementation(implementation)
    );
    const implementedStaticSchemaInterfaces = settings.isEnabled
      ? [
          ...interfacesWithoutFreezeThaw,
          {
            interfaceId: FREEZE_THAW_TRACKING_SCHEMA_INTERFACE_ID,
            limit: null,
          },
        ]
      : interfacesWithoutFreezeThaw;

    this.updateStateAndSchema({implementedStaticSchemaInterfaces});
  };

  private getFreezeThawInterfaceImplementation = (): FreezeThawSettings => {
    const freezeThawStaticSchemaInterface = this.state.implementedStaticSchemaInterfaces.find((value) =>
      isFreezeThawInterfaceImplementation(value)
    ) as FreezeThawStaticSchemaInterface;
    return {isEnabled: freezeThawStaticSchemaInterface != null, limit: null};
  };

  private maybeRenderFreezeThawConfiguration = (): JSX.Element | null => {
    if (this.state.isContainable && BenchlingConfig.INVENTORY_ENABLE_FREEZE_THAW) {
      return (
        <Row className={css(styles.inventorySettingsRow)}>
          <Col sm={12}>
            <FreezeThawCheckbox
              isWritable={
                this.isWritable() &&
                // Freeze/Thaw config is only writable if the schema is new or we are adding the interface
                (this.props.tagSchemaId == null || !this.getFreezeThawInterfaceImplementation().isEnabled)
              }
              onUpdate={this.handleFreezeThawInterfaceImplementation}
              settings={this.getFreezeThawInterfaceImplementation()}
            />
          </Col>
        </Row>
      );
    }
    return null;
  };

  private handleUpdateTrialSampleInterface = (checked: boolean): void => {
    const interfacesWithoutSample = (this.state.implementedStaticSchemaInterfaces ?? []).filter(
      (implementedInterface) => implementedInterface.interfaceId !== TRIAL_SAMPLE_SCHEMA_INTERFACE_ID
    );
    const implementedStaticSchemaInterfaces = checked
      ? [...interfacesWithoutSample, {interfaceId: TRIAL_SAMPLE_SCHEMA_INTERFACE_ID}]
      : interfacesWithoutSample;
    this.updateStateAndSchema({implementedStaticSchemaInterfaces});
  };

  private handleUpdateTrialSubjectInterface = (isChecked: boolean): void => {
    const interfacesWithoutSubject = (this.state.implementedStaticSchemaInterfaces ?? []).filter(
      (implementedInterface) => implementedInterface.interfaceId !== TRIAL_SUBJECT_SCHEMA_INTERFACE_ID
    );
    const implementedStaticSchemaInterfaces = isChecked
      ? [
          ...interfacesWithoutSubject,
          {
            interfaceId: TRIAL_SUBJECT_SCHEMA_INTERFACE_ID,
          },
        ]
      : interfacesWithoutSubject;

    this.updateStateAndSchema({implementedStaticSchemaInterfaces});
  };

  private hasImplementedInterface(staticInterfaceId: string): boolean {
    return (this.state.implementedStaticSchemaInterfaces ?? []).some(
      (implementedInterface) => implementedInterface.interfaceId === staticInterfaceId
    );
  }

  private renderTrialSubjectConfiguration = (): JSX.Element => (
    // Trial Subjects are needed for the Trial Sample ingestion workflow.
    <>
      {BenchlingConfig.REGISTRY_ENABLE_TRIAL_SAMPLES &&
        this.state.itemType === TagSchemaItemType.BASIC_FOLDER_ITEM && (
          <div className={css(styles.exclusiveSsiCheckbox)}>
            <TrialSubjectCheckbox
              disabled={
                !this.isWritable() ||
                // Trial Subject config is only writable if the schema is new or we are adding the interface
                (this.props.tagSchemaId != null &&
                  this.hasImplementedInterface(TRIAL_SUBJECT_SCHEMA_INTERFACE_ID))
              }
              onChange={this.handleUpdateTrialSubjectInterface}
              isChecked={this.hasImplementedInterface(TRIAL_SUBJECT_SCHEMA_INTERFACE_ID)}
            />
          </div>
        )}
    </>
  );

  private maybeRenderTrialSampleConfiguration = (): JSX.Element | null => {
    if (
      BenchlingConfig.REGISTRY_ENABLE_TRIAL_SAMPLES &&
      this.state.isContainable &&
      this.getFolderItemType() != null &&
      TRIAL_SAMPLE_SUPPORTED_TYPES.includes(this.getFolderItemType()!)
    ) {
      return (
        <Row className={css(styles.inventorySettingsRow)}>
          <Col sm={12}>
            <TrialSampleCheckbox
              disabled={
                !this.isWritable() ||
                // Trial Sample config is only writable if the schema is new or we are adding the interface
                (this.props.tagSchemaId != null &&
                  this.hasImplementedInterface(TRIAL_SAMPLE_SCHEMA_INTERFACE_ID))
              }
              onChange={this.handleUpdateTrialSampleInterface}
              checked={this.hasImplementedInterface(TRIAL_SAMPLE_SCHEMA_INTERFACE_ID)}
            />
          </Col>
        </Row>
      );
    }
    return null;
  };

  private renderAntibodyConfiguration = (): JSX.Element => {
    const antibodyInterfaceImplementation = this.getAntibodyInterfaceImplementation();
    return (
      <>
        {BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENTITIES && antibodyInterfaceImplementation != null && (
          <AntibodySchemaConfiguration
            antibodyInterfaceImplementation={antibodyInterfaceImplementation}
            // TODO(BNCH-84194): Writes are temporarily disabled on pre-existing schemas until the backend
            // supports static schema interface updates.
            disabled={!this.isWritable() || this.props.tagSchemaId != null}
            onChange={this.handleUpdateStaticSchemaInterfaceData}
            allSchemas={this.props.tagSchemas}
          />
        )}
      </>
    );
  };

  private renderComplexPolymerConfiguration = (): JSX.Element => {
    const complexPolymerInterfaceImplementation = this.getComplexPolymerInterfaceImplementation();
    return (
      <>
        {BenchlingConfig.MODBIO_ENABLE_COMPOSITE_ENTITIES &&
          complexPolymerInterfaceImplementation != null && (
            <ComplexPolymerSchemaConfiguration
              complexPolymerConfig={complexPolymerInterfaceImplementation}
              // TODO(BNCH-84194): Writes are temporarily disabled on pre-existing schemas until the backend
              // supports static schema interface updates.
              isWritable={this.isWritable() && this.props.tagSchemaId == null}
              onUpdateComplexPolymerConfig={this.handleUpdateStaticSchemaInterfaceData}
              schemas={this.props.tagSchemas}
            />
          )}
      </>
    );
  };

  private isExpirationInterfaceImplemented = (): boolean =>
    (this.state.implementedStaticSchemaInterfaces ?? []).some(
      ({interfaceId}) => interfaceId === EXPIRATION_TRACKING_ENTITY_SCHEMA_INTERFACE_ID
    );

  private handleUpdateExpirationInterface = (isEnabled: boolean): void => {
    const interfacesWithoutExpiration = (this.state.implementedStaticSchemaInterfaces ?? []).filter(
      ({interfaceId}) => interfaceId !== EXPIRATION_TRACKING_ENTITY_SCHEMA_INTERFACE_ID
    );
    const implementedStaticSchemaInterfaces = isEnabled
      ? [...interfacesWithoutExpiration, {interfaceId: EXPIRATION_TRACKING_ENTITY_SCHEMA_INTERFACE_ID}]
      : interfacesWithoutExpiration;
    this.updateStateAndSchema({implementedStaticSchemaInterfaces});
  };

  private maybeRenderExpirationInterface = (): JSX.Element | null => {
    if (BenchlingConfig.INVENTORY_ENABLE_EXPIRATION_DATE && this.state.isContainable) {
      return (
        <Row className={css(styles.inventorySettingsRow)}>
          <Col sm={12}>
            <ExpirationCheckbox
              // Expiration interface is only writable if the schema is new or we are adding the interface
              isWritable={
                this.isWritable() &&
                (this.props.tagSchemaId == null || !this.isExpirationInterfaceImplemented())
              }
              isChecked={this.isExpirationInterfaceImplemented()}
              onUpdate={this.handleUpdateExpirationInterface}
            />
          </Col>
        </Row>
      );
    }
    return null;
  };

  private maybeRenderExclusiveInterfaces = (): JSX.Element => (
    // Exclusive SSIs are mutually exclusive and can only apply to custom entities.
    <>
      {this.renderAntibodyConfiguration()}
      {this.renderComplexPolymerConfiguration()}
      {this.renderTrialSubjectConfiguration()}
    </>
  );

  private renderFileTypePopover = (
    folderItemType?: TagSchemaSupportedFolderItemType,
    sequenceType?: SequenceSchemaType | null
  ): JSX.Element => (
    <div className="tagSchemaSettings-popover">
      <p>This is the type of entity that can use your schema.</p>
      <p>
        This can be an existing entity type, or one that you create yourself. For instance, a "Plasmid" schema
        might have an entity type of "DNA", while a "Strain" schema would have an entity type of "Custom
        Entity".
      </p>
      {folderItemType === ModelTypeConstants.SEQUENCE && sequenceType === SequenceSchemaType.MIXED && (
        <p>
          A schema with an entity type of "Sequence (DNA or Oligo)" has entities with both "DNA Sequence" and
          "Oligo" types.
        </p>
      )}
    </div>
  );

  private maybeRenderMixtureSchemaConfiguration(): JSX.Element | null {
    if (this.state.itemType !== TagSchemaItemType.MIXTURE) {
      return null;
    }
    return (
      <MixtureSchemaConfiguration
        // If folder item type is "mixture", this is guaranteed to be non-null
        mixtureSchemaConfig={this.state.mixtureSchemaConfig!}
        isWritable={this.isWritable()}
        handleChange={this.handleChangeMixtureSchemaConfig}
      />
    );
  }

  private hasRegistrySupport(): boolean {
    const folderItemType = this.getFolderItemType();
    return !folderItemType || isRegistrySupportedType(folderItemType);
  }

  private showEnterpriseRegistryFeatures(): boolean {
    return this.hasRegistrySupport() && this.props.registry.useStrictValidation;
  }

  private renderPrefixNameAndSQLIdentifierFields(
    isBeingCreated: boolean,
    prefixSource: Optional<PrefixSource>
  ): JSX.Element {
    const prefixPopover = (
      <div className="tagSchemaSettings-popover">
        <p>Used to generate IDs when entities are registered (ex. "p" will generate IDs like pAB001)</p>
      </div>
    );

    const hasPrefixSource = prefixSource != null; // Intentionally != to check for undefined
    let editableFieldSiblingComponent: JSX.Element | undefined;
    if (hasPrefixSource) {
      editableFieldSiblingComponent = <SetBySchemaInterfaceTooltip schemaInterfaceIdAndName={prefixSource} />;
    }
    return (
      <Row>
        <Col sm={HEADER_COL_SIZE}>
          <SettingsField
            helpIcon={
              <SettingsPopover renderContent={() => prefixPopover} placement={FloatingPlacement.BOTTOM} />
            }
            isBeingCreated={isBeingCreated && !hasPrefixSource}
            isRequired={true}
            isWritable={!hasPrefixSource && this.isWritable()}
            label="Prefix"
            name="prefix"
            onChange={this.handleChangePrefix}
            value={this.state.prefix}
            dataTestAttrs={{component: 'SettingsField', element: 'input', key: 'prefix'}}
            editableFieldSiblingComponent={editableFieldSiblingComponent}
          />
        </Col>
        <Col sm={isBeingCreated ? HEADER_COL_SIZE * 2 : HEADER_COL_SIZE}>
          <SettingsField
            isBeingCreated={isBeingCreated}
            isRequired={true}
            isWritable={this.isWritable()}
            label="Name"
            name="name"
            onChange={this.handleChangeName}
            value={this.state.name}
            dataTestAttrs={{component: 'SettingsField', element: 'input', key: 'name'}}
          />
        </Col>
        {!isBeingCreated && (
          <Col sm={HEADER_COL_SIZE}>
            <SchemaWarehouseNameField
              onChange={this.handleChangeSqlIdentifier}
              isWritable={this.isWritable() && !isBeingCreated}
              sqlIdentifier={this.props.tagSchema!.sqlIdentifier}
            />
          </Col>
        )}
      </Row>
    );
  }

  private handleSchemaInterfaceSubmission(params: AddSchemaInterfaceRequestSubmitParams): void {
    const id = params.schemaInterface.id;
    const {fields, prefix, labelingStrategies, itemType, nameTemplate} = params.conformityUpdate;

    const interfaceEnforcesPrefix = prefix && prefix.length > 0;
    const interfaceEnforcesLabelingStrategies = labelingStrategies && labelingStrategies.length > 0;
    const interfaceEnforcesNameTemplate = nameTemplate && nameTemplate.parts.length > 0;

    const newState = {
      schemaInterfaceIds: this.state.schemaInterfaceIds.concat([id]),
      fields,
      prefix: interfaceEnforcesPrefix ? prefix : this.state.prefix,
      prefixSource: interfaceEnforcesPrefix ? params.schemaInterface : this.state.prefixSource,
      labelingStrategies: interfaceEnforcesLabelingStrategies
        ? labelingStrategies
        : this.state.labelingStrategies,
      labelingStrategiesOverrideSource: interfaceEnforcesLabelingStrategies
        ? params.schemaInterface
        : this.state.labelingStrategiesOverrideSource,
      itemType: itemType || this.state.itemType,
      nameTemplateParts: interfaceEnforcesNameTemplate ? nameTemplate.parts : this.state.nameTemplateParts,
      nameTemplatesOverrideSource: interfaceEnforcesNameTemplate
        ? params.schemaInterface
        : this.state.nameTemplatesOverrideSource,
      nameTemplatesOverrideFields: interfaceEnforcesNameTemplate
        ? nameTemplate.fieldsUsedInParts
        : this.state.nameTemplatesOverrideFields,
    };

    this.updateStateAndSchema(newState);
  }

  private handleSchemaInterfaceRemoval(id: string): void {
    // We remove the applied fieldset designation from the interface fields
    // and the computed fields from it, which depend on the interface to be created.
    const fields = this.state.fields
      .filter((field) => !(field.appliedFieldset?.id === id && field.isComputed))
      .map((field) => (field.appliedFieldset?.id === id ? {...field, appliedFieldset: null} : field));

    const prefixSource = this.state.prefixSource?.id === id ? null : this.state.prefixSource;
    const labelingStrategiesOverrideSource =
      this.state.labelingStrategiesOverrideSource?.id === id
        ? null
        : this.state.labelingStrategiesOverrideSource;
    const nameTemplatesOverrideSource =
      this.state.nameTemplatesOverrideSource?.id === id ? null : this.state.nameTemplatesOverrideSource;
    const nameTemplatesOverrideFields =
      this.state.nameTemplatesOverrideSource?.id === id ? null : this.state.nameTemplatesOverrideFields;

    const newState = {
      schemaInterfaceIds: this.state.schemaInterfaceIds.filter((i: string) => i !== id),
      fields,
      prefixSource,
      labelingStrategiesOverrideSource,
      nameTemplatesOverrideSource,
      nameTemplatesOverrideFields,
    };

    this.updateStateAndSchema(newState);
  }

  private handleChangeSchemaDescription(value: string): void {
    this.updateStateAndSchema({schemaDescription: value});
  }

  override render(): JSX.Element {
    const isBeingCreated = this.props.tagSchemaId == null;

    const {folderItemType, sequenceType} = this.state.itemType
      ? itemTypeToFolderItemTypeAndSequenceType(this.state.itemType)
      : {
          folderItemType: undefined,
          sequenceType: null,
        };

    return (
      <div>
        <Row>
          <Col sm={1}>
            <div className={css(styles.iconPickerPadding)}>
              <IconPicker
                value={this.state.iconId}
                onChange={this.handleChangeIcon}
                itemType={this.state.itemType}
                disabled={!this.isWritable()}
              />
            </div>
          </Col>
          <Col sm={11}>
            <div className="settingsPageContainer-section">
              <div>
                {this.renderPrefixNameAndSQLIdentifierFields(isBeingCreated, this.state.prefixSource)}
                {this.showEnterpriseRegistryFeatures() && (
                  <Row>
                    <Col sm={HEADER_COL_SIZE}>
                      {(BenchlingConfig.REGISTRY_ENABLE_GENERATE_RANDOM_REGISTRY_IDS ||
                        this.state.useRandomOrgAlias) && (
                        <label className={classNames(css(styles.checkboxLabel), 'u-tiny-spacing-bottom')}>
                          <input
                            disabled={!this.isWritable()}
                            onChange={this.handleChangeUseRandomOrgAlias}
                            ref="_useRandomOrgAliasCheckbox"
                            type="checkbox"
                            checked={this.state.useRandomOrgAlias}
                          />
                          <span className="u-normal-font-weight">
                            Generate random Registry IDs (deprecated)
                          </span>
                        </label>
                      )}

                      {this.showEnterpriseRegistryFeatures() && (
                        <>
                          <label className={classNames(css(styles.checkboxLabel), 'u-tiny-spacing-bottom')}>
                            <input
                              disabled={!this.isWritable()}
                              onChange={this.handleChangeUseOrganizationRegistryIdentifierForDisplayLabel}
                              type="checkbox"
                              checked={this.state.useOrganizationCollectionAliasForDisplayLabel}
                            />
                            <span className="u-normal-font-weight">Use Registry ID as display label</span>
                          </label>

                          <div className="u-flex-display u-tiny-spacing-bottom">
                            <div>
                              <input
                                disabled={!this.isWritable()}
                                onChange={this.handleChangeIncludeRegistryIdInChips}
                                type="checkbox"
                                name="includeRegistryIdInChips"
                                id="includeRegistryIdInChips"
                                checked={this.state.includeRegistryIdInChips ?? false}
                              />
                            </div>
                            <div className="u-tiny-spacing-left">
                              <label
                                className={classNames(css(styles.checkboxLabel), 'u-normal-font-weight')}
                                htmlFor="includeRegistryIdInChips"
                              >
                                Include Registry ID in chips
                              </label>
                              <div>
                                <Chip
                                  className={this.state.includeRegistryIdInChips ? 'ui-state-disabled' : null}
                                  size={ChipSize.MINI}
                                  text={
                                    <span>
                                      <a>
                                        <BenchlingIcon icon={this.state.iconId} />
                                        {' Example'}
                                      </a>
                                    </span>
                                  }
                                />
                                <Chip
                                  className={this.state.includeRegistryIdInChips ? null : 'ui-state-disabled'}
                                  size={ChipSize.MINI}
                                  text={
                                    <span>
                                      <a>
                                        <BenchlingIcon icon={this.state.iconId} />
                                        {' Example'}
                                      </a>
                                    </span>
                                  }
                                  secondaryText="ID"
                                />
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </Col>
                    {/* Do not display the name template section during tag schema creation, unless it is set by an interface */}
                    {(this.state.nameTemplatesOverrideSource != null ||
                      (!isBeingCreated && this.props.tagSchemaId != null)) && (
                      <Col sm={8}>
                        <EntityNameTemplateField
                          nameTemplateParts={this.state.nameTemplateParts}
                          schemaFields={this.state.nameTemplatesOverrideFields ?? this.state.fields}
                          schemaId={this.props.tagSchemaId ?? this.state.nameTemplatesOverrideSource?.id!}
                          bioentityType={folderItemType || ''}
                          sequenceType={sequenceType}
                          isWritable={this.isWritable() && this.state.nameTemplatesOverrideSource == null}
                          setNameTemplateParts={this.setNameTemplateParts}
                          shouldOrderNamePartsBySequence={this.state.shouldOrderNamePartsBySequence}
                          labelingStrategies={this.state.labelingStrategies}
                          nameTemplatesOverrideSource={this.state.nameTemplatesOverrideSource}
                          itemType={this.state.itemType}
                        />
                      </Col>
                    )}
                  </Row>
                )}
                <SchemaDescriptionInput
                  value={this.state.schemaDescription ?? null}
                  onChange={(description: string) => {
                    this.handleChangeSchemaDescription(description);
                  }}
                  isWritable={this.isWritable()}
                  isBeingCreated={isBeingCreated}
                />
                {folderItemType && TagSchemaUtil.entityTypeSupportsResidues(folderItemType) && (
                  <label className={css(styles.checkboxLabel)}>
                    <input
                      disabled={!this.isWritable()}
                      onChange={this.handleChangeShowResidues}
                      ref="_showResiduesCheckbox"
                      type="checkbox"
                      checked={this.state.showResidues}
                    />
                    <span className="u-normal-font-weight">
                      {`Show ${FileUtil.getResidueWord(folderItemType)}s in expanded view`}
                    </span>
                  </label>
                )}
                {BenchlingConfig.ENABLE_SCHEMA_CREATE_AS_OLIGO &&
                  this.state.itemType === TagSchemaItemType.MIXED && (
                    <div>
                      <input
                        className="_test-shouldCreateAsOligoCheckbox"
                        disabled={!this.isWritable()}
                        onChange={this.handleChangeShouldCreateAsOligo}
                        type="checkbox"
                        checked={this.state.shouldCreateAsOligo}
                      />
                      <span className="u-tiny-spacing-left">Create entities as oligos by default</span>
                    </div>
                  )}
              </div>
            </div>
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <div className="settingsPageContainer-section">
              <SettingsLabel
                helpIcon={
                  <SettingsPopover
                    title="Entity type"
                    renderContent={() => this.renderFileTypePopover(folderItemType, sequenceType)}
                  />
                }
                isRequired={true}
                label="Entity type"
              />
              <div className="tagSchemaSettings-typeSelector u-position-relative">
                <TagSchemaItemTypeSelector
                  displayName={
                    this.state.itemType ? getItemTypeDisplayName(this.state.itemType) : 'Select a type'
                  }
                  entityOptionConfiguration={{
                    // The mixed type is deprecated so only add if it is already selected
                    includeMixed: this.state.itemType === TagSchemaItemType.MIXED,
                    includeMixture: this.state.itemType === TagSchemaItemType.MIXTURE,
                  }}
                  onChangeItemType={this.handleChangeFileType}
                  selectedItemType={this.state.itemType}
                  // Require user to drop uniqueResidue/uniqueCanonicalSmiles constraints to switch file types
                  disabled={
                    (this.props.tagSchemaId != null && !isEditableType(this.state.itemType)) ||
                    (this.state.constraint != null && this.state.constraint.uniqueResidues) ||
                    (this.state.constraint != null && this.state.constraint.uniqueCanonicalSmiles) ||
                    !this.isWritable()
                  }
                />
              </div>
            </div>
            {this.maybeRenderExclusiveInterfaces()}
            <div className="u-large-spacing-bottom">
              <InterfaceConstraintErrorsBannerStack
                interfaceConstraintErrors={this.state.interfaceConstraintErrors}
                onClickAction={(errorData: InterfaceConstraintErrorDisplayDataForSchema) => {
                  if (errorData.id) {
                    navigateToURL(schemaInterfaceURL(errorData.id));
                  }
                }}
                actionText={`View ${schemaFieldsetName()}`}
                errorsToRender={SCHEMA_OR_INTERFACE_LEVEL_ERRORS}
              />
            </div>

            {this.maybeRenderMixtureSchemaConfiguration()}
            {this.showEnterpriseRegistryFeatures() && (
              <ConstraintTable
                constraint={this.state.constraint}
                folderItemType={folderItemType || ''}
                onAddClick={this.handleOpenConstraintModal}
                onRemoveClick={this.handleRemoveConstraint}
                writable={this.isConstraintWritable()}
                tableType={ConstraintTableType.SCHEMA}
              />
            )}
            {BenchlingConfig.SCHEMAS_ENABLE_INTERFACES &&
              this.state.itemType !== TagSchemaItemType.ENTRY &&
              (this.props.tagSchemaId ? (
                <EditSchemaInterfaceTable
                  schemaId={this.props.tagSchemaId}
                  organizationId={this.props.registry.ownerJson.id}
                  updateSchemaInterfacesCallbackFn={() => this.props.refetchTagSchemaFileSettings!()}
                  writable={this.isWritable()}
                  setInterfaceConstraintErrors={this.setInterfaceConstraintErrors}
                  tagSchema={{fields: this.state.fields}}
                />
              ) : (
                <CreateSchemaInterfaceTable
                  writable={this.isWritable()}
                  organizationId={this.props.registry.ownerJson.id}
                  queuedSchemaInterfaces={this.state.schemaInterfaceIds}
                  onSchemaInterfaceEnqueue={(params: AddSchemaInterfaceRequestSubmitParams) => {
                    this.handleSchemaInterfaceSubmission(params);
                    return Promise.resolve();
                  }}
                  onSchemaInterfaceDequeue={(id: string) => {
                    this.handleSchemaInterfaceRemoval(id);
                    return Promise.resolve();
                  }}
                  tagSchema={{fields: this.state.fields}}
                />
              ))}
            {isBeingCreated && (
              // Display SSI entity field requirements only for schemas newly being created.
              // When editing an existing schema, changes to SSIs that require changes
              // to entity fields will surface as errors.
              // Display even if the schema fulfills the SSI entity field requirements,
              // to ensure the user knows what the fields will be used for.
              <StaticInterfaceEntityFieldsBannerStack
                selectedInterfaceIds={this.state.implementedStaticSchemaInterfaces.map(
                  (ssi) => ssi.interfaceId
                )}
              />
            )}
            {this.renderFileFields(folderItemType, sequenceType)}
            {this.renderHoistedFields()}
            <div className="u-large-spacing-bottom">
              <InterfaceConstraintErrorsBannerStack
                interfaceConstraintErrors={this.state.interfaceConstraintErrors}
                onClickAction={(errorData: InterfaceConstraintErrorDisplayDataForSchema) => {
                  if (errorData.id) {
                    window.open(schemaInterfaceURL(errorData.id), '_blank');
                  }
                }}
                actionText={`View ${schemaFieldsetName()}`}
                errorsToRender={FIELD_LEVEL_ERRORS}
              />
            </div>
            {BenchlingConfig.REGISTRY_ENABLE_CHILD_ENTITIES &&
              this.props.tagSchema &&
              this.props.tagSchema.childEntityLinkConfigs.length > 0 && (
                <ChildEntitySchemaTable
                  parentSchema={this.props.tagSchema}
                  tagSchemas={this.props.tagSchemas}
                  ownerUrlId={this.props.ownerUrlId}
                  registryId={this.props.registryId}
                />
              )}
            {isRegistryEmbeddingEnabled() && this.props.tagSchemaId != null && this.hasRegistrySupport() && (
              <div className="u-large-spacing-bottom">
                <TagSchemaDashboardLinkConfigTable
                  isWritable={this.isWritable()}
                  tagSchemaId={this.props.tagSchemaId}
                />
              </div>
            )}
            {this.isBioentitySchema() && (
              <div className="metadata-table simpleDivTable u-large-spacing-bottom">
                <div className="simpleDivTable-header">
                  <div className="u-small-spacing-bottom simpleDivTable-headerCell">Inventory settings</div>
                </div>
                <div className={css(styles.inventorySettingsPadding)}>
                  <TagSchemaSampleTypeConfiguration
                    isContainable={this.state.isContainable}
                    isWritable={this.isWritable()}
                    onChange={(isContainable: boolean, containableType: TagSchemaContainableType) => {
                      let implementedStaticSchemaInterfaces =
                        this.state.implementedStaticSchemaInterfaces ?? [];
                      if (!isContainable) {
                        if (BenchlingConfig.INVENTORY_ENABLE_FREEZE_THAW) {
                          implementedStaticSchemaInterfaces = implementedStaticSchemaInterfaces.filter(
                            (implementation) => !isFreezeThawInterfaceImplementation(implementation)
                          );
                        }
                        if (BenchlingConfig.INVENTORY_ENABLE_EXPIRATION_DATE) {
                          implementedStaticSchemaInterfaces = implementedStaticSchemaInterfaces.filter(
                            (implementation) =>
                              implementation.interfaceId !== EXPIRATION_TRACKING_ENTITY_SCHEMA_INTERFACE_ID
                          );
                        }
                      }
                      this.updateStateAndSchema({
                        isContainable,
                        containableType,
                        implementedStaticSchemaInterfaces,
                      });
                    }}
                    // TODO: handle undefined this.state.folderItemType
                    folderItemType={folderItemType!}
                  />
                  {this.showEnterpriseRegistryFeatures() && this.state.isContainable && !isBeingCreated && (
                    <Row>
                      <Col sm={8}>
                        <ContainerNameTemplateField
                          bioentityType={folderItemType || ''}
                          sequenceType={sequenceType}
                          schemaFields={this.state.fields}
                          schemaId={this.props.tagSchemaId}
                          isWritable={this.isWritable()}
                          nameTemplateParts={this.state.containerNameTemplateParts}
                          setNameTemplateParts={this.setContainerNameTemplateParts}
                        />
                      </Col>
                    </Row>
                  )}
                  {this.maybeRenderTrialSampleConfiguration()}
                  {this.maybeRenderFreezeThawConfiguration()}
                  {this.maybeRenderExpirationInterface()}
                </div>
              </div>
            )}
            {this.showEnterpriseRegistryFeatures() && (
              <div className="settingsPageContainer-section">
                <LabelingStrategyConfiguration
                  labelingStrategies={this.state.labelingStrategies}
                  labelingStrategiesOverrideSource={this.state.labelingStrategiesOverrideSource}
                  onChange={this.handleChangeLabelingStrategies}
                  nameTemplateParts={this.state.nameTemplateParts}
                  isWritable={this.isWritable() && this.state.labelingStrategiesOverrideSource == null}
                />
              </div>
            )}
          </Col>
        </Row>
      </div>
    );
  }
}

function graphQLChildEntityLinkConfigsToProps(
  childEntityLinkConfigs: Array<QueryChildEntityLinkConfig>
): Array<ChildEntityLinkConfig> {
  return childEntityLinkConfigs.map((linkConfig) => ({
    childEntitySchemaId: linkConfig.childEntitySchema.id,
    childSchemaLinkField: linkConfig.childSchemaLinkFieldJson,
  }));
}

function graphQLStaticSchemaInterfaceImplementationToProps(
  implementation:
    | QueryImplementedAntibodySchemaInterface
    | QueryComplexPolymerSchemaConfigurationData
    | QueryImplementedEquipmentSchemaInterface
): ImplementedStaticSchemaInterface {
  assert(
    !isInaccessible(implementation),
    'Expected interface implementation to be accessible when schema is accessible'
  );
  const {__typename, id, schemaInterface, ...otherFields} = implementation;
  // TODO(BNCH-122973): We should reduce or eliminate the dependency on modbio code here, via
  // a system to register parsing functions for different implemented interfaces.
  if (__typename === 'ImplementedAntibodySchemaInterfaceNode') {
    const otherFields = {
      formatId: implementation.format.id,
      domainSchemaFieldSpecs: implementation.domainLinkedSchemas,
      chainSchemaFieldSpecs: implementation.chainLinkedSchemas,
    };
    return {
      interfaceId: schemaInterface.id,
      ...otherFields,
    };
  } else if (__typename === 'OligoConjugateSchemaConfigNode') {
    const otherFields = {
      oligoSchemaFieldSpec: {
        schemaFieldName: implementation.oligoSchemaField.name,
        schemaOrInterfaceId: implementation.oligoSchemaField.schemaId,
      },
      conjugatesSchemaFieldSpecs: implementation.conjugatesSchemaFields.map((field) => ({
        schemaFieldName: field.name,
        schemaOrInterfaceId: field.schemaId,
      })),
    };
    return {
      interfaceId: schemaInterface.id,
      ...otherFields,
    };
  }
  if (__typename === 'OligoDuplexSchemaConfigNode') {
    const otherFields = {
      reverseStrandSchemaFieldSpec: {
        schemaFieldName: implementation.reverseStrandSchemaField.name,
        schemaOrInterfaceId: implementation.reverseStrandSchemaField.schemaId,
      },
      forwardStrandSchemaFieldSpec: {
        schemaFieldName: implementation.forwardStrandSchemaField.name,
        schemaOrInterfaceId: implementation.forwardStrandSchemaField.schemaId,
      },
      conjugatesSchemaFieldSpecs: implementation.conjugatesSchemaFields.map((field) => ({
        schemaFieldName: field.name,
        schemaOrInterfaceId: field.schemaId,
      })),
    };
    return {
      interfaceId: schemaInterface.id,
      ...otherFields,
    };
  }
  return {interfaceId: schemaInterface.id, ...otherFields};
}

function queryTagSchemaFieldsToPropFieldsForRules(
  queryTagSchemaFields: Array<SchemaField>,
  schemaFieldApiIdToSimpleRules: Record<string, Array<SimpleRule>>,
  schemaFieldApiIdToAdvancedRules: Record<string, Array<ValidationRule>>
): Array<SchemaField> {
  return queryTagSchemaFields.map((field) => {
    const rules = {
      simpleRules: schemaFieldApiIdToSimpleRules.hasOwnProperty(field.apiId)
        ? schemaFieldApiIdToSimpleRules[field.apiId]
        : [],
      advancedRules: schemaFieldApiIdToAdvancedRules.hasOwnProperty(field.apiId)
        ? schemaFieldApiIdToAdvancedRules[field.apiId]
        : [],
    };
    return {
      ...field,
      ...rules,
    };
  });
}

function convertRulesForQueryTagSchemaToPropTagSchema(
  tagSchema: TagSchema,
  simpleRules: Array<SimpleRuleGraphQL>,
  advancedRules: Array<AdvancedRuleGraphQL>
): TagSchema {
  const schemaFieldApiIdToSimpleRules: Record<string, Array<SimpleRule>> = {};
  const schemaFieldApiIdToAdvancedRules: Record<string, Array<ValidationRule>> = {};
  for (const simpleRule of simpleRules) {
    // create a mapping from field id to list of simple rules
    if (!schemaFieldApiIdToSimpleRules[simpleRule.schemaFieldApiIdentifier]) {
      schemaFieldApiIdToSimpleRules[simpleRule.schemaFieldApiIdentifier] = [];
    }
    schemaFieldApiIdToSimpleRules[simpleRule.schemaFieldApiIdentifier].push({
      validationRuleId: simpleRule.validationRuleId,
      validationRuleTemplateId: simpleRule.validationRuleTemplateId,
      constantInputs: simpleRule.constantInputs,
    });
  }
  for (const advancedRule of advancedRules) {
    const advancedRuleData = {
      id: advancedRule.id,
      name: advancedRule.name,
      userInputFormula: advancedRule.userInputFormula,
      description: advancedRule.description,
      errorMessage: advancedRule.errorMessage,
      overridable: advancedRule.overridable,
      negateResult: advancedRule.negateResult,
    };
    for (const referencedSchemaField of advancedRule.schemaFields) {
      // create a mapping from field id to list of advanced rules
      if (!schemaFieldApiIdToAdvancedRules[referencedSchemaField.apiId]) {
        schemaFieldApiIdToAdvancedRules[referencedSchemaField.apiId] = [];
      }
      schemaFieldApiIdToAdvancedRules[referencedSchemaField.apiId].push(advancedRuleData);
    }
  }
  return {
    ...tagSchema,
    fields: queryTagSchemaFieldsToPropFieldsForRules(
      tagSchema.fields,
      schemaFieldApiIdToSimpleRules,
      schemaFieldApiIdToAdvancedRules
    ),
    allFields: queryTagSchemaFieldsToPropFieldsForRules(
      tagSchema.allFields,
      schemaFieldApiIdToSimpleRules,
      schemaFieldApiIdToAdvancedRules
    ),
  };
}

/**
 * Converts a QueryTagSchema to a TagSchemaWithAdditionalQueriedAttributes
 *
 * @param queryTagSchema - The QueryTagSchema to convert
 * @param v1ImplementedStaticSchemaInterfaces - The static schema interfaces implemented in V1
 * This includes the ComplexPolymerSchemaConfigurationData and ImplementedEquipmentSchemaInterface which are
 * queried outside of the V2 interfaceConfigs GraphQL field.
 */
export function queryTagSchemaToPropTagSchema(
  queryTagSchema: QueryChildEntityAwareTagSchemaWithContainerNameTemplate,
  // TODO(BNCH-93302): Schemas to remove this param and unify the interfaces with v2 interfaceConfigs
  v1ImplementedStaticSchemaInterfaces?: Array<
    | QueryImplementedAntibodySchemaInterface
    | QueryComplexPolymerSchemaConfigurationData
    | QueryImplementedEquipmentSchemaInterface
  > | null
): TagSchemaWithAdditionalQueriedAttributes {
  const tagSchema = graphQLToREST('TagSchema', queryTagSchema);
  const v2InterfaceConfigs = queryTagSchema.interfaceConfigs.filter(
    (interfaceConfig) =>
      ![
        ...Object.values(ComplexPolymerInterfaceApiId),
        ANTIBODY_SCHEMA_INTERFACE_ID,
        EQUIPMENT_SCHEMA_INTERFACE_ID,
      ].includes(
        interfaceConfig.interface.id
        // Filter to static interfaces since the list of interfaces includes custom interfaces
      ) && getSchemaInterfaceType(interfaceConfig.interface.id) === DomainGraphType.STATIC_SCHEMA_INTERFACE
  );

  // Ensure that there is no overlap between V1 and V2 interface IDs since we are filtering out the V1 interfaces
  const v1InterfaceIds = (v1ImplementedStaticSchemaInterfaces ?? [])
    .map((implementation) => (!isInaccessible(implementation) ? implementation.schemaInterface.id : null))
    .filter((id) => id != null);
  const v2InterfaceIds = v2InterfaceConfigs.map((implementation) => implementation.interface.id);
  assert(
    intersect([v1InterfaceIds, v2InterfaceIds]).length === 0,
    'Expected no overlap between V1 and V2 interface IDs'
  );

  const implementedStaticSchemaInterfaces = [
    ...v2InterfaceConfigs.map((ssi) => ({interfaceId: ssi.interface.id})),
    ...(v1ImplementedStaticSchemaInterfaces ?? []).map((implementation) =>
      graphQLStaticSchemaInterfaceImplementationToProps(implementation)
    ),
  ];
  return {
    ...(isSimpleRulesEnabled()
      ? convertRulesForQueryTagSchemaToPropTagSchema(
          tagSchema,
          queryTagSchema.simpleRules,
          queryTagSchema.advancedRules
        )
      : tagSchema),
    containerNameTemplateParts: graphQLNameTemplatePartsToProps(queryTagSchema.containerNameTemplateParts),
    childEntityLinkConfigs: graphQLChildEntityLinkConfigsToProps(queryTagSchema.childEntityLinkConfigs),
    prefixSource: queryTagSchema.prefixSource,
    labelingStrategiesOverrideSource: queryTagSchema.labelingStrategiesOverrideSource,
    nameTemplatesOverrideSource: queryTagSchema.nameTemplatesOverrideSource,
    implementedStaticSchemaInterfaces,
    activeMigration: queryTagSchema.activeMigration,
    numItems: queryTagSchema.numItems,
    schemaDescription: queryTagSchema.schemaDescription,
    itemType: queryTagSchema.itemType,
    constraint: tagSchema.constraint
      ? {
          ...tagSchema.constraint,
          typeSpecificConstraintConfig: queryTagSchema.constraint?.typeSpecificConstraintConfig,
        }
      : null,
  };
}

export default function QueryingTagSchemaFileSettings(props: Props): JSX.Element | null {
  const [saving] = useTagSchemaSavingState();

  const currentUserResult = useBenchlingQuery<CurrentUserBenchlingAdminQuery>(
    CURRENT_USER_BENCHLING_ADMIN_QUERY
  );
  const tagSchemaFileSettingsResult = useSkippableBenchlingQuery<
    TagSchemaFileSettingsQuery,
    TagSchemaFileSettingsQueryVariables
  >(TAG_SCHEMA_FILE_SETTINGS_QUERY, {
    fetchPolicy: 'network-only',
    skip: props.tagSchemaId == null,
    variables: props.tagSchemaId != null ? {tagSchemaId: props.tagSchemaId} : undefined,
    renderCachedDataWhenRefetching: true,
  });
  const registrySummaryResult = useBenchlingQuery<RegistrySummaryQuery, RegistrySummaryQueryVariables>(
    REGISTRY_SUMMARY_QUERY,
    {
      variables: {registryId: props.registryId},
    }
  );
  const tagSchemaSummariesResult = useTagSchemaSummariesListingQuery({registryId: props.registryId});
  const allStorableSchemasResult = useBenchlingQuery<AllStorableSchemasQuery>(ALL_STORABLE_SCHEMAS_QUERY);

  if (
    currentUserResult.type === QueryResultType.PENDING ||
    tagSchemaFileSettingsResult.type === QueryResultType.PENDING ||
    registrySummaryResult.type === QueryResultType.PENDING ||
    tagSchemaSummariesResult.type === QueryResultType.PENDING ||
    allStorableSchemasResult.type === QueryResultType.PENDING
  ) {
    return <HorizontalThrobber />;
  } else if (
    currentUserResult.type === QueryResultType.SUCCESS &&
    (tagSchemaFileSettingsResult.type === QueryResultType.SUCCESS ||
      tagSchemaFileSettingsResult.type === QueryResultType.SKIPPED) &&
    registrySummaryResult.type === QueryResultType.SUCCESS &&
    tagSchemaSummariesResult.type === QueryResultType.SUCCESS &&
    allStorableSchemasResult.type === QueryResultType.SUCCESS
  ) {
    const queryTagSchemas = tagSchemaSummariesResult.data.tagSchemasListing.tagSchemas;

    const queryBoxSchemas = allStorableSchemasResult.data.boxSchemas.filter(
      (schema) => schema.registry.id === props.registryId
    );
    const queryContainerSchemas = allStorableSchemasResult.data.containerSchemas.filter(
      (schema) => schema.registry.id === props.registryId
    );
    const queryLocationSchemas = allStorableSchemasResult.data.locationSchemas.filter(
      (schema) => schema.registry.id === props.registryId
    );
    const queryPlateSchemas = allStorableSchemasResult.data.plateSchemas.filter(
      (schema) => schema.registry.id === props.registryId
    );
    assert(
      !(tagSchemaFileSettingsResult.data && isInaccessible(tagSchemaFileSettingsResult.data.tagSchema)),
      `Expecting to edit a TagSchema, but it was inaccessible (tagSchemaId=${props.tagSchemaId})`
    );
    assert(!isInaccessible(registrySummaryResult.data.registry), 'Expecting registry to be accessible');
    assert(
      currentUserResult.data.currentUser?.__typename === 'UserNode',
      `Non-user should not access this page.`
    );

    // Using `!` because TS doesn't respect the filter to non-null.  This is only safe once the filter has
    // been applied.
    // TODO(BNCH-93302): Schemas to remove this param and unify the interfaces with v2 interfaceConfigs
    const v1ImplementedStaticSchemaInterfaces = [
      tagSchemaFileSettingsResult.data?.implementedAntibodySchemaInterface!,
      tagSchemaFileSettingsResult.data?.complexPolymerSchemaConfiguration!,
      tagSchemaFileSettingsResult.data?.implementedEquipmentSchemaInterface!,
    ].filter((impl) => impl != null);
    return (
      <TagSchemaFileSettings
        {...props}
        saving={saving}
        tagSchema={
          props.tagSchemaId &&
          tagSchemaFileSettingsResult.data &&
          !isInaccessible(tagSchemaFileSettingsResult.data.tagSchema)
            ? queryTagSchemaToPropTagSchema(
                tagSchemaFileSettingsResult.data.tagSchema,
                v1ImplementedStaticSchemaInterfaces
              )
            : null
        }
        refetchTagSchemaFileSettings={async () => await tagSchemaFileSettingsResult.refetch()}
        isBenchlingAdmin={currentUserResult.data.currentUser.isBenchlingAdmin}
        tagSchemas={queryTagSchemas}
        storableSchemas={[
          ...graphQLArrayToREST('PlateSchema', queryPlateSchemas),
          ...graphQLArrayToREST('BoxSchema', queryBoxSchemas),
          ...graphQLArrayToREST('ContainerSchema', queryContainerSchemas),
          ...graphQLArrayToREST('LocationSchema', queryLocationSchemas),
        ]}
        registry={registrySummaryResult.data.registry}
      />
    );
  } else {
    return null;
  }
}

const styles = StyleSheet.create({
  inventorySettingsRow: {
    paddingTop: '24px',
  },
  inventorySettingsPadding: {
    padding: '16px',
  },
  iconPickerPadding: {
    marginRight: '24px',
  },
  checkboxLabel: {
    display: 'block',
  },
  errorBannerStack: {
    marginBottom: '8px',
  },
  exclusiveSsiCheckbox: {
    marginTop: '-22px',
  },
});

// Header column size is in units of sm-4
const HEADER_COL_SIZE = 4;
